import {
  Box,
  Divider,
  Heading,
  HStack,
  Icon,
  IconButton,
  Skeleton,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
} from '@elkaso-app/web-design';
import { useGetSiSheetsOverviewApi, useRefreshSiSheetApi } from 'apis/supplier-intelligence';
import { FiBox, FiClipboard, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { IconType } from 'react-icons/lib';
import { SheetName } from '../utils/types';

interface StatCardProps {
  icon: IconType;
  iconColor: string;
  title: string;
  description: string;
  stats: { label: string; value: number }[];
  breakdowns: { label: string; data: Record<string, number> }[];
  cardBg: string;
  borderColor: string;
  sheetName: SheetName;
}

const StatCard = ({ icon, iconColor, title, description, stats, breakdowns, cardBg, borderColor, sheetName }: StatCardProps) => {
  const { mutate: refreshSheet, isLoading: isRefreshing } = useRefreshSiSheetApi();

  return (
    <Box flex={1} p={6} bg={cardBg} border='1px solid' borderColor={borderColor} borderRadius='xl' shadow='sm'>
      <VStack spacing={3} align='stretch'>
        <HStack spacing={3} justify='space-between'>
          <HStack spacing={3}>
            <Icon as={icon} boxSize={5} color={iconColor} />
            <Heading size='sm'>{title}</Heading>
          </HStack>
          <Tooltip label={`Refresh ${title.toLowerCase()} sheet`} placement='top'>
            <IconButton
              aria-label={`Refresh ${title.toLowerCase()} sheet`}
              icon={<FiRefreshCw />}
              size='xs'
              variant='ghost'
              isLoading={isRefreshing}
              onClick={() => refreshSheet(sheetName)}
            />
          </Tooltip>
        </HStack>
        <Text fontSize='xs' color='gray.500'>
          {description}
        </Text>

        <Divider />

        {stats.map((s) => (
          <HStack key={s.label} justify='space-between'>
            <Text fontSize='sm' color='gray.600'>
              {s.label}
            </Text>
            <Text fontSize='sm' fontWeight='semibold'>
              {(s.value ?? 0).toLocaleString()}
            </Text>
          </HStack>
        ))}

        {breakdowns.map((b) => (
          <Box key={b.label}>
            <Text fontSize='xs' fontWeight='semibold' color='gray.500' textTransform='uppercase' mb={1}>
              {b.label}
            </Text>
            {Object.entries(b.data ?? {}).map(([key, val]) => (
              <HStack key={key} justify='space-between' py={0.5}>
                <Text fontSize='xs' color='gray.600'>
                  {key}
                </Text>
                <Text fontSize='xs' fontWeight='medium'>
                  {(val ?? 0).toLocaleString()}
                </Text>
              </HStack>
            ))}
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

const SkeletonCard = ({ cardBg, borderColor }: { cardBg: string; borderColor: string }) => (
  <Box flex={1} p={6} bg={cardBg} border='1px solid' borderColor={borderColor} borderRadius='xl' shadow='sm'>
    <VStack spacing={3} align='stretch'>
      <Skeleton height='20px' width='120px' />
      <Skeleton height='14px' width='200px' />
      <Divider />
      <Skeleton height='16px' />
      <Skeleton height='16px' />
      <Skeleton height='16px' />
      <Skeleton height='16px' />
    </VStack>
  </Box>
);

export const SheetsOverviewSection = () => {
  const { data, isLoading, isError } = useGetSiSheetsOverviewApi();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  if (isError) return null;

  if (isLoading) {
    return (
      <HStack spacing={6} align='stretch' mt={6}>
        <SkeletonCard cardBg={cardBg} borderColor={borderColor} />
        <SkeletonCard cardBg={cardBg} borderColor={borderColor} />
        <SkeletonCard cardBg={cardBg} borderColor={borderColor} />
      </HStack>
    );
  }

  if (!data) return null;

  return (
    <HStack spacing={6} align='stretch' mt={6}>
      <StatCard
        icon={FiUsers}
        iconColor='purple.500'
        title='Contacts'
        description='Supplier contacts and their engagement status'
        stats={[
          { label: 'Total Rows', value: data.contacts.totalRows },
          { label: 'Unique Suppliers', value: data.contacts.uniqueSuppliers },
        ]}
        breakdowns={[
          { label: 'By Status', data: data.contacts.byStatus },
          { label: 'By Stage', data: data.contacts.byStage },
        ]}
        cardBg={cardBg}
        borderColor={borderColor}
        sheetName='contacts'
      />

      <StatCard
        icon={FiBox}
        iconColor='green.500'
        title='Products'
        description='Product catalog across suppliers and markets'
        stats={[
          { label: 'Total Rows', value: data.products.totalRows },
          { label: 'Unique Suppliers', value: data.products.uniqueSuppliers },
          { label: 'Unique Items', value: data.products.uniqueItems },
        ]}
        breakdowns={[
          { label: 'By Category', data: data.products.byCategoryL1 },
          { label: 'By Country', data: data.products.byCountry },
        ]}
        cardBg={cardBg}
        borderColor={borderColor}
        sheetName='products'
      />

      <StatCard
        icon={FiClipboard}
        iconColor='orange.500'
        title='Catalogs'
        description='Supplier onboarding pipeline and catalog status'
        stats={[
          { label: 'Total Rows', value: data.catalogs.totalRows },
          { label: 'Unique Suppliers', value: data.catalogs.uniqueSuppliers },
        ]}
        breakdowns={[
          { label: 'By Pipeline Status', data: data.catalogs.byPipelineStatus },
          { label: 'By Supplier Type', data: data.catalogs.bySupplierType },
          { label: 'By Priority', data: data.catalogs.byPriority },
        ]}
        cardBg={cardBg}
        borderColor={borderColor}
        sheetName='catalogs'
      />
    </HStack>
  );
};
